// How an entity is named in an address: the id, and beside it the type.
//
// Ids of the product plane are opaque, so the type travels with the id. An
// organization, facility, project or source is the exception: its id is
// written without a type, as it always was, and which of the four it names is
// told from the fetched response.
//
// The module is pure: it knows nothing of the router or of React.

import { getEntityTypeEntry } from "../types/entityRegistry";

// The query parameter of the id of the entity the overlay shows
export const DETAIL_ENTITY_ID_PARAM = "detail_entity_id";

// The query parameter of that entity's type, beside the id
export const DETAIL_ENTITY_TYPE_PARAM = "detail_entity_type";

// The query parameter of the open relationship; it and the entity exclude
// each other
export const DETAIL_RELATIONSHIP_ID_PARAM = "detail_relationship_id";

// The query parameter of the type on the full-page route `/entity/:entityId`
export const ENTITY_PAGE_TYPE_PARAM = "type";

// An entity as an address names it
export interface EntityAddress {
  entityId: string;
  // The singular type key; undefined for an id that comes without a type
  entityType?: string;
}

// The type as an address writes it: none for a type whose id is resolved
// without one, the type itself for every other, also for one the registry
// does not know yet.
export const addressEntityType = (
  entityType: string | null | undefined,
): string | undefined => {
  if (!entityType) {
    return undefined;
  }
  const entry = getEntityTypeEntry(entityType);
  return entry && !entry.typeInAddress ? undefined : entityType;
};

// The address of an entity's full page
export const entityPath = (
  entityId: string,
  entityType?: string | null,
): string => {
  const type = addressEntityType(entityType);
  // An id that comes without a type is written as it always was
  return type
    ? `/entity/${encodeURIComponent(entityId)}?${ENTITY_PAGE_TYPE_PARAM}=${encodeURIComponent(type)}`
    : `/entity/${entityId}`;
};

// The entity the overlay of an address shows; null when it shows none
export const readDetailEntity = (
  params: URLSearchParams,
): EntityAddress | null => {
  const entityId = params.get(DETAIL_ENTITY_ID_PARAM);
  if (!entityId) {
    return null;
  }
  return {
    entityId,
    entityType: params.get(DETAIL_ENTITY_TYPE_PARAM) || undefined,
  };
};

// The address with the entity opened in the overlay. Every other parameter
// is kept, but for the open relationship; `params` itself is not changed.
export const withDetailEntity = (
  params: URLSearchParams,
  entityId: string,
  entityType?: string | null,
): URLSearchParams => {
  const next = new URLSearchParams(params);
  next.delete(DETAIL_RELATIONSHIP_ID_PARAM);
  next.set(DETAIL_ENTITY_ID_PARAM, entityId);
  const type = addressEntityType(entityType);
  if (type) {
    next.set(DETAIL_ENTITY_TYPE_PARAM, type);
  } else {
    next.delete(DETAIL_ENTITY_TYPE_PARAM);
  }
  return next;
};

// The address with the overlay closed: neither id nor type
export const withoutDetailEntity = (
  params: URLSearchParams,
): URLSearchParams => {
  const next = new URLSearchParams(params);
  next.delete(DETAIL_ENTITY_ID_PARAM);
  next.delete(DETAIL_ENTITY_TYPE_PARAM);
  return next;
};

// The relationship the overlay of an address shows; null when it shows none
export const readDetailRelationship = (
  params: URLSearchParams,
): string | null => params.get(DETAIL_RELATIONSHIP_ID_PARAM) || null;

// The address with the relationship opened in the overlay. The entity, id
// and type, leaves it; every other parameter is kept, and `params` itself
// is not changed.
export const withDetailRelationship = (
  params: URLSearchParams,
  relationshipId: string,
): URLSearchParams => {
  const next = withoutDetailEntity(params);
  next.set(DETAIL_RELATIONSHIP_ID_PARAM, relationshipId);
  return next;
};

// The address with the relationship's overlay closed
export const withoutDetailRelationship = (
  params: URLSearchParams,
): URLSearchParams => {
  const next = new URLSearchParams(params);
  next.delete(DETAIL_RELATIONSHIP_ID_PARAM);
  return next;
};
